import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { StatusBadge } from '../components/common/StatusBadge';
import { PriorityBadge } from '../components/common/PriorityBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, ClipboardList, PlayCircle, CheckCircle, XCircle } from 'lucide-react';
import { getWorkOrders, getVehicles, createWorkOrder, updateWorkOrder, deleteWorkOrder } from '../lib/api';
import { formatDate, formatRelativeTime } from '../lib/utils';
import { toast } from 'sonner';

const CATEGORIES = [
    { value: 'mesin_transmisi', label: 'Mesin & Transmisi' },
    { value: 'sistem_rem', label: 'Sistem Rem' },
    { value: 'ban_velg', label: 'Ban & Velg' },
    { value: 'tangki_pompa', label: 'Tangki & Pompa' },
    { value: 'flowmeter', label: 'Flowmeter' },
    { value: 'kelistrikan', label: 'Kelistrikan' },
    { value: 'body_chassis', label: 'Body & Chassis' },
    { value: 'safety', label: 'Safety Equipment' },
    { value: 'dokumen', label: 'Dokumen' }
];

const STATUSES = ['pending', 'in-progress', 'completed', 'cancelled'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];

export default function WorkOrdersPage() {
    const [workOrders, setWorkOrders] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [costDialogOpen, setCostDialogOpen] = useState(false);
    const [editingWorkOrder, setEditingWorkOrder] = useState(null);
    const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
    const [costData, setCostData] = useState({ biaya_sparepart: '', biaya_jasa: '', catatan: '' });
    const [formData, setFormData] = useState({
        vehicle_id: '',
        judul: '',
        kategori: 'mesin_transmisi',
        deskripsi: '',
        priority: 'medium',
        assigned_to: '',
        odometer_saat_service: '',
        due_date: ''
    });

    useEffect(() => {
        fetchData();
    }, [statusFilter, priorityFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            if (priorityFilter !== 'all') params.priority = priorityFilter;
            
            const [workOrdersRes, vehiclesRes] = await Promise.all([
                getWorkOrders(params),
                getVehicles()
            ]);
            setWorkOrders(workOrdersRes.data);
            setVehicles(vehiclesRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Gagal memuat data');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (workOrder = null) => {
        if (workOrder) {
            setEditingWorkOrder(workOrder);
            setFormData({
                vehicle_id: workOrder.vehicle_id || '',
                judul: workOrder.judul || '',
                kategori: workOrder.kategori || 'mesin_transmisi',
                deskripsi: workOrder.deskripsi || '',
                priority: workOrder.priority || 'medium',
                assigned_to: workOrder.assigned_to || '',
                odometer_saat_service: workOrder.odometer_saat_service || '',
                due_date: workOrder.due_date ? workOrder.due_date.split('T')[0] : ''
            });
        } else {
            setEditingWorkOrder(null);
            setFormData({
                vehicle_id: '',
                judul: '',
                kategori: 'mesin_transmisi',
                deskripsi: '',
                priority: 'medium',
                assigned_to: '',
                odometer_saat_service: '',
                due_date: ''
            });
        }
        setDialogOpen(true);
    };

    const handleVehicleChange = (vehicleId) => {
        const vehicle = vehicles.find(v => v.id === vehicleId);
        setFormData({
            ...formData,
            vehicle_id: vehicleId,
            odometer_saat_service: vehicle?.odometer || ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
                odometer_saat_service: formData.odometer_saat_service ? parseFloat(formData.odometer_saat_service) : null
            };

            if (editingWorkOrder) {
                await updateWorkOrder(editingWorkOrder.id, submitData);
                toast.success('Work Order berhasil diupdate');
            } else {
                await createWorkOrder(submitData);
                toast.success('Work Order berhasil dibuat');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to save work order:', error);
            toast.error('Gagal menyimpan Work Order');
        }
    };

    const handleStatusChange = async (workOrder, newStatus) => {
        try {
            if (newStatus === 'completed') {
                setSelectedWorkOrder(workOrder);
                setCostData({
                    biaya_sparepart: workOrder.biaya_sparepart || '',
                    biaya_jasa: workOrder.biaya_jasa || '',
                    catatan: workOrder.catatan || ''
                });
                setCostDialogOpen(true);
            } else {
                await updateWorkOrder(workOrder.id, { status: newStatus });
                toast.success(`Work Order ${newStatus === 'in-progress' ? 'dimulai' : 'diupdate'}`);
                fetchData();
            }
        } catch (error) {
            console.error('Failed to update status:', error);
            toast.error('Gagal update status');
        }
    };

    const handleCompleteWithCost = async () => {
        try {
            await updateWorkOrder(selectedWorkOrder.id, {
                status: 'completed',
                biaya_sparepart: parseFloat(costData.biaya_sparepart) || 0,
                biaya_jasa: parseFloat(costData.biaya_jasa) || 0,
                catatan: costData.catatan
            });
            toast.success('Work Order selesai');
            setCostDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to complete work order:', error);
            toast.error('Gagal menyelesaikan Work Order');
        }
    };

    const handleDelete = async (workOrder) => {
        if (window.confirm(`Yakin ingin menghapus "${workOrder.judul}"?`)) {
            try {
                await deleteWorkOrder(workOrder.id);
                toast.success('Work Order berhasil dihapus');
                fetchData();
            } catch (error) {
                console.error('Failed to delete work order:', error);
                toast.error('Gagal menghapus Work Order');
            }
        }
    };

    const filteredWorkOrders = workOrders.filter(wo =>
        wo.judul.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wo.work_order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (wo.vehicle_nomor_polisi && wo.vehicle_nomor_polisi.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const columns = [
        {
            header: 'WO Number',
            accessor: 'work_order_number',
            render: (value) => (
                <span className="font-mono text-xs uppercase tracking-wider text-primary font-medium">
                    {value}
                </span>
            )
        },
        {
            header: 'Kendaraan',
            accessor: 'vehicle_nomor_polisi',
            render: (value) => (
                <span className="font-mono text-xs">{value}</span>
            )
        },
        {
            header: 'Pekerjaan',
            accessor: 'judul',
            render: (value, row) => (
                <div>
                    <p className="font-medium">{value}</p>
                    <p className="text-xs text-muted-foreground capitalize">{row.kategori?.replace('_', ' ')}</p>
                </div>
            )
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (value) => <StatusBadge status={value} />
        },
        {
            header: 'Priority',
            accessor: 'priority',
            render: (value) => <PriorityBadge priority={value} />
        },
        {
            header: 'Due Date',
            accessor: 'due_date',
            render: (value) => (
                <div>
                    <p className="text-sm">{formatDate(value)}</p>
                    <p className="text-xs text-muted-foreground">{formatRelativeTime(value)}</p>
                </div>
            )
        },
        {
            header: 'Total Biaya',
            accessor: 'total_biaya',
            render: (value) => value ? <span className="text-sm">Rp {value?.toLocaleString()}</span> : '-'
        },
        {
            header: '',
            accessor: 'actions',
            render: (_, row) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`wo-actions-${row.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {row.status === 'pending' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(row, 'in-progress')}>
                                <PlayCircle className="h-4 w-4 mr-2" />
                                Mulai Kerja
                            </DropdownMenuItem>
                        )}
                        {row.status === 'in-progress' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(row, 'completed')}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Selesai
                            </DropdownMenuItem>
                        )}
                        {(row.status === 'pending' || row.status === 'in-progress') && (
                            <DropdownMenuItem onClick={() => handleStatusChange(row, 'cancelled')}>
                                <XCircle className="h-4 w-4 mr-2" />
                                Batalkan
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleOpenDialog(row)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            onClick={() => handleDelete(row)}
                            className="text-destructive focus:text-destructive"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Hapus
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        }
    ];

    return (
        <Layout title="Work Orders">
            <div className="space-y-6" data-testid="work-orders-page">
                {/* Header Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="flex flex-1 gap-3 w-full sm:w-auto flex-wrap">
                        <div className="relative flex-1 sm:w-64 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Cari work order..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 rounded-sm"
                                data-testid="search-work-orders"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[140px] rounded-sm" data-testid="status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Status</SelectItem>
                                {STATUSES.map(status => (
                                    <SelectItem key={status} value={status}>
                                        {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                            <SelectTrigger className="w-[140px] rounded-sm" data-testid="priority-filter">
                                <SelectValue placeholder="Priority" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Priority</SelectItem>
                                {PRIORITIES.map(p => (
                                    <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button 
                        onClick={() => handleOpenDialog()} 
                        className="industrial-btn-primary"
                        data-testid="add-work-order-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Buat Work Order
                    </Button>
                </div>

                {/* Work Orders Table */}
                <DataTable
                    columns={columns}
                    data={filteredWorkOrders}
                    loading={loading}
                    emptyMessage="Tidak ada work order"
                    emptyIcon={ClipboardList}
                />

                {/* Add/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="section-title">
                                {editingWorkOrder ? 'Edit Work Order' : 'Buat Work Order Baru'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingWorkOrder ? 'Update detail work order.' : 'Buat work order maintenance baru.'}
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-2 gap-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="vehicle_id">Kendaraan *</Label>
                                    <Select 
                                        value={formData.vehicle_id} 
                                        onValueChange={handleVehicleChange}
                                    >
                                        <SelectTrigger className="rounded-sm" data-testid="wo-vehicle-select">
                                            <SelectValue placeholder="Pilih kendaraan" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {vehicles.map(v => (
                                                <SelectItem key={v.id} value={v.id}>{v.nomor_polisi} - {v.merk}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="judul">Judul Pekerjaan *</Label>
                                    <Input
                                        id="judul"
                                        value={formData.judul}
                                        onChange={(e) => setFormData({ ...formData, judul: e.target.value })}
                                        required
                                        className="rounded-sm"
                                        data-testid="wo-judul-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="kategori">Kategori *</Label>
                                    <Select 
                                        value={formData.kategori} 
                                        onValueChange={(value) => setFormData({ ...formData, kategori: value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map(c => (
                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="priority">Prioritas</Label>
                                    <Select 
                                        value={formData.priority} 
                                        onValueChange={(value) => setFormData({ ...formData, priority: value })}
                                    >
                                        <SelectTrigger className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PRIORITIES.map(p => (
                                                <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="due_date">Due Date *</Label>
                                    <Input
                                        id="due_date"
                                        type="date"
                                        value={formData.due_date}
                                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                                        required
                                        className="rounded-sm"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="odometer_saat_service">Odometer (km)</Label>
                                    <Input
                                        id="odometer_saat_service"
                                        type="number"
                                        value={formData.odometer_saat_service}
                                        onChange={(e) => setFormData({ ...formData, odometer_saat_service: e.target.value })}
                                        className="rounded-sm font-mono"
                                    />
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="assigned_to">Ditugaskan Ke</Label>
                                    <Input
                                        id="assigned_to"
                                        value={formData.assigned_to}
                                        onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                                        className="rounded-sm"
                                        placeholder="Bengkel A"
                                    />
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <Label htmlFor="deskripsi">Deskripsi</Label>
                                    <Textarea
                                        id="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={(e) => setFormData({ ...formData, deskripsi: e.target.value })}
                                        className="rounded-sm"
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                                    Batal
                                </Button>
                                <Button type="submit" className="industrial-btn-primary" data-testid="save-wo-btn">
                                    {editingWorkOrder ? 'Update' : 'Simpan'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Complete with Cost Dialog */}
                <Dialog open={costDialogOpen} onOpenChange={setCostDialogOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="section-title">Selesaikan Work Order</DialogTitle>
                            <DialogDescription>
                                Masukkan biaya untuk {selectedWorkOrder?.work_order_number}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4 space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="biaya_sparepart">Biaya Sparepart (Rp)</Label>
                                <Input
                                    id="biaya_sparepart"
                                    type="number"
                                    value={costData.biaya_sparepart}
                                    onChange={(e) => setCostData({ ...costData, biaya_sparepart: e.target.value })}
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="biaya_jasa">Biaya Jasa (Rp)</Label>
                                <Input
                                    id="biaya_jasa"
                                    type="number"
                                    value={costData.biaya_jasa}
                                    onChange={(e) => setCostData({ ...costData, biaya_jasa: e.target.value })}
                                    className="rounded-sm"
                                />
                            </div>
                            {(costData.biaya_sparepart || costData.biaya_jasa) && (
                                <div className="p-3 bg-primary/10 rounded-sm">
                                    <div className="flex justify-between text-sm font-medium">
                                        <span>Total Biaya:</span>
                                        <span className="font-mono">
                                            Rp {((parseFloat(costData.biaya_sparepart) || 0) + (parseFloat(costData.biaya_jasa) || 0)).toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label htmlFor="catatan_complete">Catatan</Label>
                                <Textarea
                                    id="catatan_complete"
                                    value={costData.catatan}
                                    onChange={(e) => setCostData({ ...costData, catatan: e.target.value })}
                                    className="rounded-sm"
                                    rows={2}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setCostDialogOpen(false)} className="rounded-sm">
                                Batal
                            </Button>
                            <Button onClick={handleCompleteWithCost} className="industrial-btn-primary">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Selesaikan
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
